package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.Composable
import com.siriusxm.pia.components.Badge
import com.siriusxm.pia.components.BadgeColor
import com.siriusxm.pia.components.MessageType
import com.siriusxm.pia.components.confirmationDialogWithContent
import com.siriusxm.pia.components.messageBox
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import unifiedcontent.aggregator.PermissionModel

/**
 * The permission model a producer record effectively has. The aggregator reads an absent value as
 * LEGACY (catalog PR #710), so the console does too; an unrecognised value is shown as itself.
 */
internal fun PermissionModel?.effective(): PermissionModel = this ?: PermissionModel.LEGACY

/** The values the producer editor offers. */
internal val selectablePermissionModels: List<PermissionModel> = listOf(PermissionModel.LEGACY, PermissionModel.UCP)

/** A one-line explanation of a permission model, for the editor and the detail page. */
internal fun PermissionModel.description(): String = when (this) {
    PermissionModel.UCP ->
        "Partial updates and relationship edits made through this producer are authorized per user by " +
            "Unified Content Permissions (AVP). Admin group membership does not bypass it."

    PermissionModel.LEGACY ->
        "Partial updates and relationship edits are authorized by this producer's service users and " +
            "the admin groups."

    else -> "Unrecognised permission model. The aggregator treats it as LEGACY."
}

/**
 * Whether changing a producer's model from [from] to [to] needs a typed confirmation. Any real change
 * does: it moves who may publish as the producer from one system to the other, and moving to UCP
 * takes away the admin bypass. The editor leaves the value unchanged by default.
 */
internal fun permissionModelChangeNeedsConfirmation(from: PermissionModel?, to: PermissionModel?): Boolean =
    from.effective().name != to.effective().name

/**
 * A small green badge naming a producer's permission model, shown on the producer detail page only for
 * UCP producers, so an admin can see that the producer's human-invoked partial and relationship calls
 * are authorized through AVP. LEGACY is the default and is deliberately not badged.
 *
 * @param model the producer's `permissionModel`
 */
@Composable
fun PermissionModelBadge(model: PermissionModel?) {
    Badge(model.effective().name, BadgeColor.GREEN)
}

/**
 * A confirmation dialog shown when an edit would change a producer's permission model. It explains
 * what changes and requires the producer id to be typed, as disabling a producer does, because the
 * change takes effect on the next intake call and can lock users out (for example, admins lose their
 * bypass under UCP until an AVP policy grants them).
 *
 * @param producerId the producer being changed, also the confirmation text
 * @param from the current model
 * @param to the model the edit would set
 * @param onConfirm invoked with true to proceed with the save, false to cancel it
 */
@Composable
fun PermissionModelChangeDialog(
    producerId: String,
    from: PermissionModel?,
    to: PermissionModel,
    onConfirm: suspend (Boolean) -> Unit
) {
    confirmationDialogWithContent(
        title = "Change Permission Model",
        action = "Change to ${to.name}",
        confirmationText = producerId,
        confirmationInstruction = "Type '$producerId' to confirm",
        cb = onConfirm
    ) { _, _ ->
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            messageBox(MessageType.WARNING) {
                Text("This changes how $producerId authorizes people, from ${from.effective().name} to ${to.name}.")
            }
            Div({ style { fontSize(0.9.em) } }) {
                Text(to.description())
            }
            if (to == PermissionModel.UCP) {
                Div({ style { fontSize(0.9.em) } }) {
                    Text(
                        "Before switching, make sure the UnifiedContentAggregator_PartialUpdate and " +
                            "UnifiedContentAggregator_RelationshipUpdate actions and their policies exist, and " +
                            "that this producer's allowed partials and relationships are complete. Otherwise " +
                            "people get 403 or 503 errors, or partials are accepted and then silently dropped."
                    )
                }
            }
        }
    }
}
